import { EXTRACT_OCR_STATUS } from "core/consts";
import aiApi from "core/model/api/endpoints/extractDocs";
import { getErrorMessage } from "core/model/utils/errors";
import {
  ExtractOCRResult,
  ExtractOCRStatus,
  ExtractOCRStatusResponse,
  FileCategory,
  FileState,
} from "core/types";
// eslint-disable-next-line no-outside-core-imports/no-outside-core-imports
import { useToast } from "dsl/atoms/ToastNotificationContext";
// #TODO: move useHandleFileDownload into core or inject from app
// eslint-disable-next-line no-outside-core-imports/no-outside-core-imports
import { useHandleFileDownload } from "dsl/ecosystems/PatientFiles/useDownloadFile";
import { isFileHISStateful, isFileV2 } from "core/model/files/utils";
import { useCallback, useEffect, useState } from "react";
import { useTranslations } from "translations";

type OCRResultsByFileId = Record<string | number, ExtractOCRResult>;

export const useExtractOCR = ({
  auctionId,
  careseekerId,
  consultationId,
  externalId,
  ocrStatus,
  onComplete,
  patientId,
}: {
  careseekerId: number | undefined;
  patientId: number;
  auctionId: number | undefined;
  consultationId: number | undefined;
  externalId: string | undefined;
  onComplete: () => Promise<ExtractOCRStatusResponse | null>;
  ocrStatus: ExtractOCRStatusResponse | null;
}) => {
  const [results, setResults] = useState<OCRResultsByFileId>({});
  const [errors, setErrors] = useState<Array<Record<string, string>>>([]);
  const [extractingAllFiles, setExtractingAllFiles] = useState(false);
  const toast = useToast();
  const translations = useTranslations();

  const [processingFileIds, setProcessingFileIds] = useState<
    Array<string | number>
  >([]);

  const handleDownload = useHandleFileDownload({
    externalId: externalId ?? "",
    target: undefined,
  });

  const handleOcrForFile = useCallback(
    async ({
      file,
      fileCategory,
      id,
    }: {
      file: File;
      fileCategory: FileCategory;
      id: number;
    }) => {
      setProcessingFileIds((prev) => [...prev, id]);

      try {
        if (!careseekerId || !patientId) {
          throw new Error("careseekerId or patientId is not defined");
        }
        if (!auctionId && !consultationId) {
          throw new Error("auctionId or consultationId is not defined");
        }
        const ocrResult = await aiApi.extract.ocr({
          auctionId,
          careseekerId,
          consultationId,
          file,
          fileCategory,
          fileId: String(id),
          patientId,
        });

        setResults((prev) => ({
          ...prev,
          [id]: ocrResult,
        }));

        await onComplete();

        return ocrResult;
      } finally {
        setProcessingFileIds((prev) => prev.filter((fileId) => fileId !== id));
      }
    },
    [auctionId, consultationId, setProcessingFileIds, setResults, onComplete],
  );

  const downloadAndRunOcr = useCallback(
    async (file: FileState): Promise<ExtractOCRResult> => {
      if (!careseekerId || !patientId) {
        throw new Error("careseekerId or patientId is not defined");
      }

      if (!auctionId && !consultationId) {
        throw new Error("auctionId or consultationId is not defined");
      }

      const downloadResult = await handleDownload(file);
      if (!downloadResult) {
        throw new Error("Failed to download file - no result returned");
      }
      if (!downloadResult.pdfBytes) {
        throw new Error("No PDF bytes returned from download");
      }

      const ocrResult = await aiApi.extract.ocr({
        auctionId,
        careseekerId,
        consultationId,
        file: new File(
          [downloadResult.pdfBytes as BlobPart],
          downloadResult.fileName,
          {
            type: "application/pdf",
          },
        ),
        fileCategory: isFileHISStateful(file)
          ? file.document_category_number
          : file.category,
        fileId: String(file.id),
        patientId,
      });

      setResults((prev) => ({
        ...prev,
        [file.id]: ocrResult,
      }));

      return ocrResult;
    },
    [auctionId, consultationId, careseekerId, patientId, handleDownload],
  );

  const handleDownloadAndRunOcr = useCallback(
    async ({
      extractAllFiles,
      files,
    }: {
      files: FileState[];
      extractAllFiles?: boolean;
    }) => {
      // Files that should be excluded from extraction
      const excludedStatuses = [
        EXTRACT_OCR_STATUS.EXTRACTED,
        EXTRACT_OCR_STATUS.PROCESSING,
      ] as ExtractOCRStatus[];

      // get the documents that have already been extracted or are being processed for exclusion
      const excludedFileIds = ocrStatus?.documents
        .filter(
          (doc) => doc.recare_file_id && excludedStatuses.includes(doc.status),
        )
        .map((doc) => doc.recare_file_id);

      const filesToExtract = files.filter((file) => {
        // only extract non-HIS files
        if (!isFileV2(file) || file.kis_document_id_string) return false;

        // if the file has a kis document id string, it is a HIS file, so we should not extract it
        if (file.kis_document_id_string) return false;

        // if the file is excluded by its status or not having a recare file id, we should not extract it
        return !excludedFileIds?.includes(file.id);
      });
      if (!filesToExtract.length) return;

      setErrors([]);
      setExtractingAllFiles(!!extractAllFiles);
      setProcessingFileIds((prev) => [
        ...prev,
        ...filesToExtract.map((f) => f.id),
      ]);

      try {
        await Promise.all(
          filesToExtract.map(async (file) => {
            try {
              await downloadAndRunOcr(file);
            } catch (err) {
              const message = getErrorMessage(err);
              console.error(
                `error with extract ocr for file ${file.id}`,
                message,
              );
              toast({
                color: "danger",
                message: translations.general.error,
              });
              setErrors((prev) => [
                ...prev,
                { fileId: String(file.id), message },
              ]);
            }
          }),
        );

        await onComplete();
      } finally {
        setProcessingFileIds((prev) =>
          prev.filter((id) => !filesToExtract.some((file) => file.id === id)),
        );
        setExtractingAllFiles(false);
      }
    },
    [downloadAndRunOcr, onComplete],
  );

  useEffect(() => {
    return () => {
      setProcessingFileIds([]);
      setErrors([]);
      setExtractingAllFiles(false);
    };
  }, []);

  return {
    errors,
    extractingAllFiles,
    handleDownloadAndRunOcr,
    handleOcrForFile,
    processingFileIds,
    results,
  };
};

export type UseExtractOCRReturn = ReturnType<typeof useExtractOCR>;
